import Image from 'app/Components/Shared/Image/image';
import React, {useState, useEffect, useRef} from 'react';
import {connect} from 'react-redux';
import {useDispatch} from 'react-redux';
import './SearchPage.scss';
import SearchField from '../../Components/SearchField/SearchField';
import PlaceholderImage from './Images/music.svg';
import ErrorPage from '../ErrorPage/ErrorPage';
import SongList from '../../Components/SongList/SongList';
import {doSearch, clearSearchResults, setSearchQuery, saveToHistory} from '../../FuntoneSlice';
import Loader from 'app/Components/Shared/Loader/Loader';
import {isMobile} from 'react-device-detect';
import {audioPlayer} from '../../Utils/AudioPlayer';
import {
  dwhFuntoneSearchDisplayed,
  dwhFuntoneSearchResultsDisplayed,
  dwhFuntoneNoSearchResults,
} from '../../Reports/dwhReports';
import {getCommonReportParams} from '../../Reports/reportHelpers';

const SearchPage = (props) => {
  const dispatch = useDispatch();
  const [inputText, setInputText] = useState('');
  const [searchCounter, setSearchCounter] = useState(0);
  const [currentPlayingSongId, setCurrentPlayingSongId] = useState(null);
  const lastReportedSearch = useRef('');
  const lastReportedResultsCount = useRef(null);
  const reportDebounceTimer = useRef(null);
  const currentSearchId = useRef(0); // Track current search to ignore stale results
  const lastSearchedQuery = useRef(''); // Track what query was last actually searched

  const {searchResults, isLoading, searchQuery} = props.search;

  // Save to history on mount
  useEffect(() => {
    return () => {
      props.clearSearchResults();
    };
  }, [dispatch]);

  // Report search displayed on mount
  useEffect(() => {
    if (props.selectedSubscriber && props.loginData) {
      const reportParams = getCommonReportParams(props.selectedSubscriber, props.loginData);
      dwhFuntoneSearchDisplayed({
        ...reportParams,
        ban: props.loginData?.userDetails?.ban || '',
        crmId: props.loginData?.userDetails?.crmId || '',
      });
    }
  }, [props.selectedSubscriber, props.loginData]);

  // Subscribe to audio player updates
  useEffect(() => {
    const unsubscribe = audioPlayer.subscribe((playingSongId, playing) => {
      if (playing && playingSongId) {
        setCurrentPlayingSongId(playingSongId);
      } else {
        setCurrentPlayingSongId(null);
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    // Initialize with search query from Redux if it exists
    if (searchQuery && searchQuery !== inputText) {
      setInputText(searchQuery);
      // Auto-search if query is long enough
      if (searchQuery.length >= 3) {
        currentSearchId.current += 1;
        const searchId = currentSearchId.current;
        console.warn('🔄 [INIT] Initializing with Redux searchQuery:', searchQuery, '| searchId:', searchId);
        performSearch(searchQuery, searchId);
      }
    }
  }, []); // Run only once on mount, don't re-run on searchQuery changes

  // Debounced search effect - waits 400ms after user stops typing
  useEffect(() => {
    // Don't search if input is too short
    if (inputText.length < 3) {
      if (searchCounter > 0 && inputText.length === 0) {
        setSearchCounter(0);
        clearSearch();
      }
      return;
    }

    // Increment search ID to invalidate any pending searches
    currentSearchId.current += 1;
    const searchId = currentSearchId.current;
    
    // Set up debounce timer
    const debounceTimer = setTimeout(() => {
      // Only perform search if this is still the latest search
      if (searchId === currentSearchId.current) {
        performSearch(inputText, searchId);
      }
    }, 400); // Wait 400ms after user stops typing

    // Cleanup: cancel the timer if inputText changes before 400ms
    return () => {
      clearTimeout(debounceTimer);
    };
  }, [inputText]);

  // Report search results when they change - but avoid duplicates
  useEffect(() => {
    // Clear any existing debounce timer
    if (reportDebounceTimer.current) {
      clearTimeout(reportDebounceTimer.current);
    }

    if (!isLoading && inputText.length >= 3) {
      // Debounce the reporting to prevent rapid fire reports during typing
      reportDebounceTimer.current = setTimeout(() => {
        const reportParams = getCommonReportParams(props.selectedSubscriber, props.loginData);
        const resultsCount = searchResults ? searchResults.length : -1;
        const searchKey = `${inputText.trim()}_${resultsCount}`;
        
        // Only report if this exact search term + result count combination hasn't been reported
        if (lastReportedSearch.current !== searchKey) {
          if (searchResults && searchResults.length > 0) {
            // Report search results displayed
            dwhFuntoneSearchResultsDisplayed({
              ...reportParams,
              ban: props.loginData?.userDetails?.ban || '',
              crmId: props.loginData?.userDetails?.crmId || '',
            });
            lastReportedSearch.current = searchKey;
          } else if (searchResults && searchResults.length === 0) {
            // Report no search results
            dwhFuntoneNoSearchResults({
              ...reportParams,
              ban: props.loginData?.userDetails?.ban || '',
              crmId: props.loginData?.userDetails?.crmId || '',
            });
            lastReportedSearch.current = searchKey;
          }
        }
      }, 500); // 500ms debounce delay
    }

    // Cleanup function to clear timeout if component unmounts or effect runs again
    return () => {
      if (reportDebounceTimer.current) {
        clearTimeout(reportDebounceTimer.current);
      }
    };
  }, [searchResults, isLoading, inputText, props.selectedSubscriber, props.loginData]);

  const performSearch = (value, searchId) => {
    setSearchCounter(searchCounter + 1);
    lastSearchedQuery.current = value;
    props.doSearch({query: value, searchId});
  };

  const clearSearch = () => {
    currentSearchId.current += 1; // Invalidate any pending searches
    props.clearSearchResults();
    props.setSearchQuery(''); // Clear Redux search query
    setInputText('');
  };

  const handleInputChange = (value) => {
    setInputText(value);
    props.setSearchQuery(value); // Keep Redux in sync
  };

  const handleSearch = (query) => {
    console.warn('🔎 [INPUT] handleSearch (Enter pressed):', query);
    setInputText(query);
    if (query.length >= 3) {
      currentSearchId.current += 1;
      performSearch(query, currentSearchId.current);
    }
  };

  const handleSongClick = (song) => {
    // TODO: Handle song selection
  };

  const handlePlayClick = (song) => {
    const songId = song.id || song.productId;
    const previewUrl = song.funtonPrevUrl || song.previewUrl;
    
    if (!previewUrl) {
      console.warn('No preview URL available for song:', songId);
      return;
    }

    if (currentPlayingSongId === songId && audioPlayer.isPlaying(songId)) {
      audioPlayer.pause();
      setCurrentPlayingSongId(null);
    } else {
      audioPlayer.play(songId, previewUrl);
      setCurrentPlayingSongId(songId);
    }
  };

  const handleDownloadClick = (song) => {
  // Removed debug logs
    // TODO: Handle download
  };

  const renderSearchResults = () => {
    if (isLoading) {
      return (
        <div className='search-results'>
          <SongList
            songs={[]}
            isLoading={true}
            skeletonCount={3}
            onSongClick={handleSongClick}
            onPlayClick={handlePlayClick}
            onDownloadClick={handleDownloadClick}
            currentPlayingSongId={currentPlayingSongId}
          />
        </div>
      );
    }

    if (inputText.length >= 3 && searchResults && searchResults.length > 0) {
      return (
        <div className='search-results'>
          <SongList
            songs={searchResults}
            skeletonCount={3}
            onSongClick={handleSongClick}
            onPlayClick={handlePlayClick}
            onDownloadClick={handleDownloadClick}
            currentPlayingSongId={currentPlayingSongId}
          />
        </div>
      );
    }

    if (inputText.length >= 3 && searchResults && searchResults.length === 0 && !isLoading && searchCounter > 0 && inputText.trim() === lastSearchedQuery.current) {
      return (
        <ErrorPage
          errorTitle={
            <>
              לא מצאנו שיר מתאים
              <br />
              שנחפש משהו אחר?
            </>
          }
          errorMessage={
            <>
              אפשר לנסות להקליד שם אחר של שיר,
              <br />
              אמן או ז׳אנר
            </>
          }
          shouldSaveToHistory={false}
        />
      );
    }

    return null;
  };

  return (
    <div className={'funtone-screen-content'}>
      <SearchField
        placeholder='חפשו שיר, אמן או ז׳אנר...'
        onSearch={handleSearch}
        onChange={handleInputChange}
        value={inputText}
        autoFocus={true}
      />
      {!inputText && (
        <div className='search-placeholder'>
          <Image src={PlaceholderImage} alt='Search Music' />
          <span className={'placeholder-text'}>
            שיר מרים או מרגש,
            <br />
            העיקר שיבוא לך בטוב :)
            <br />
            מה בא לך שישמעו?
          </span>
        </div>
      )}

      {renderSearchResults()}
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    search: state.FuntoneSlice.search,
    selectedSubscriber: state.FuntoneSlice.selectedSubscriber,
    loginData: state.authentication.loginData,
  };
};

const mapDispatchToProps = {
  doSearch,
  clearSearchResults,
  setSearchQuery,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchPage);
